import { useCallback, useRef } from 'react';

import { useNumberIncrementHandler } from '@travel/traveler-core/components/AmountInput/hooks';

import {
  DEFAULT_ADULT,
  DEFAULT_CHILDREN_LOWER_GRADE,
  DEFAULT_CHILDREN_UPPER_GRADE,
  DEFAULT_INFANT_BED_MEAL,
  DEFAULT_INFANT_BED_ONLY,
  DEFAULT_INFANT_MEAL_ONLY,
  DEFAULT_INFANT_NONE,
  DEFAULT_ROOM,
  MAX_ADULT_COUNT,
  MAX_ADULT_COUNT_OVERSEAS,
  MAX_KID_COUNT,
  MAX_KID_COUNT_OVERSEAS,
  MAX_ROOM_COUNT,
} from 'favoriteList/components/SearchForm/utils/search';

const useAmountInputNumberHandlers = (
  roomCount?: number,
  adultCount?: number,
  childrenUpperGradeCount?: number,
  childrenLowerGradeCount?: number,
  infantBedMealCount?: number,
  infantBedCount?: number,
  infantMealCount?: number,
  infantNoneCount?: number,
  isDomestic: boolean = true,
) => {
  const defaultCounters = useRef({
    rooms: roomCount || DEFAULT_ROOM,
    adults: adultCount || DEFAULT_ADULT,
    childrenUpperGrade: childrenUpperGradeCount || DEFAULT_CHILDREN_UPPER_GRADE,
    childrenLowerGrade: childrenLowerGradeCount || DEFAULT_CHILDREN_LOWER_GRADE,
    infantsBedMeal: infantBedMealCount || DEFAULT_INFANT_BED_MEAL,
    infantsBed: infantBedCount || DEFAULT_INFANT_BED_ONLY,
    infantsMeal: infantMealCount || DEFAULT_INFANT_MEAL_ONLY,
    infantsNone: infantNoneCount || DEFAULT_INFANT_NONE,
  });

  const prevCounters = useRef({
    prevRoomCounter: defaultCounters.current.rooms,
    prevAdultCounter: defaultCounters.current.adults,
    prevChildrenUpperGradeCounter: defaultCounters.current.childrenUpperGrade,
    prevChildrenLowerGradeCounter: defaultCounters.current.childrenLowerGrade,
    prevInfantBedMealCounter: defaultCounters.current.infantsBedMeal,
    prevInfantBedCounter: defaultCounters.current.infantsBed,
    prevInfantMealCounter: defaultCounters.current.infantsMeal,
    prevInfantNoneCounter: defaultCounters.current.infantsNone,
  });

  const rooms = useNumberIncrementHandler(roomCount || DEFAULT_ROOM, undefined, 1, MAX_ROOM_COUNT);

  const adults = useNumberIncrementHandler(
    adultCount || DEFAULT_ADULT,
    undefined,
    1,
    isDomestic ? MAX_ADULT_COUNT : MAX_ADULT_COUNT_OVERSEAS,
  );

  const childrenUpperGrade = useNumberIncrementHandler(
    childrenUpperGradeCount || DEFAULT_CHILDREN_UPPER_GRADE,
    undefined,
    0,
    isDomestic ? MAX_KID_COUNT : MAX_KID_COUNT_OVERSEAS,
  );
  const childrenLowerGrade = useNumberIncrementHandler(
    childrenLowerGradeCount || DEFAULT_CHILDREN_LOWER_GRADE,
    undefined,
    0,
    isDomestic ? MAX_KID_COUNT : MAX_KID_COUNT_OVERSEAS,
  );

  const infantsBedMeal = useNumberIncrementHandler(
    infantBedMealCount || DEFAULT_INFANT_BED_MEAL,
    undefined,
    0,
    isDomestic ? MAX_KID_COUNT : MAX_KID_COUNT_OVERSEAS,
  );

  const infantsBed = useNumberIncrementHandler(
    infantBedCount || DEFAULT_INFANT_BED_ONLY,
    undefined,
    0,
    isDomestic ? MAX_KID_COUNT : MAX_KID_COUNT_OVERSEAS,
  );

  const infantsMeal = useNumberIncrementHandler(
    infantMealCount || DEFAULT_INFANT_MEAL_ONLY,
    undefined,
    0,
    isDomestic ? MAX_KID_COUNT : MAX_KID_COUNT_OVERSEAS,
  );

  const infantsNone = useNumberIncrementHandler(
    infantNoneCount || DEFAULT_INFANT_NONE,
    undefined,
    0,
    isDomestic ? MAX_KID_COUNT : MAX_KID_COUNT_OVERSEAS,
  );

  const resetPrevValue = useCallback(() => {
    prevCounters.current = {
      prevRoomCounter: rooms.counter,
      prevAdultCounter: adults.counter,
      prevChildrenUpperGradeCounter: childrenUpperGrade.counter,
      prevChildrenLowerGradeCounter: childrenLowerGrade.counter,
      prevInfantBedMealCounter: infantsBedMeal.counter,
      prevInfantBedCounter: infantsBed.counter,
      prevInfantMealCounter: infantsMeal.counter,
      prevInfantNoneCounter: infantsNone.counter,
    };
  }, [
    rooms.counter,
    adults.counter,
    childrenUpperGrade.counter,
    childrenLowerGrade.counter,
    infantsBedMeal.counter,
    infantsBed.counter,
    infantsMeal.counter,
    infantsNone.counter,
  ]);

  const resetValues = useCallback(() => {
    const defaultCountersValues = defaultCounters.current;
    rooms.set(defaultCountersValues.rooms);
    adults.set(defaultCountersValues.adults);
    childrenUpperGrade.set(defaultCountersValues.childrenUpperGrade);
    childrenUpperGrade.set(defaultCountersValues.childrenLowerGrade);
    infantsBedMeal.set(defaultCountersValues.infantsBedMeal);
    infantsBed.set(defaultCountersValues.infantsBed);
    infantsMeal.set(defaultCountersValues.infantsMeal);
    infantsNone.set(defaultCountersValues.infantsNone);
  }, [adults, childrenUpperGrade, infantsBed, infantsBedMeal, infantsMeal, infantsNone, rooms]);

  return {
    prevCounters,
    resetPrevValue,
    resetValues,
    rooms,
    adults,
    childrenUpperGrade,
    childrenLowerGrade,
    infantsNone,
    infantsBedMeal,
    infantsBed,
    infantsMeal,
  };
};

export default useAmountInputNumberHandlers;
